import React from "react";
import { Paper, Stack, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TruckPickupIcon from "@mui/icons-material/LocalShippingOutlined";

//
// MODES
//
const MODES = [
  {
    value: "self",
    label: "Self Pickup",
    caption: "Buyer collects from seller location",
    Icon: StorefrontIcon,
  },
  {
    value: "third",
    label: "Third-Party",
    caption: "Partner picks up & delivers",
    Icon: TruckPickupIcon,
  },
  {
    value: "seller",
    label: "Seller to Ship",
    caption: "Seller arranges shipping",
    Icon: LocalShippingIcon,
  },
];

//
// Styled components
//
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: "24px",
  margin: "0 auto",
  maxWidth: "1200px",
  borderRadius: "14px",
  boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
  marginBottom: "28px",
}));

const StyledModeCard = styled(Paper)(({ theme, active }) => ({
  position: "relative",
  cursor: "pointer",
  padding: "24px",
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "20px",
  borderRadius: "16px",
  overflow: "hidden",
  border: active ? "2px solid #1976d2" : "2px solid rgba(0, 0, 0, 0.08)",
  backgroundColor: active ? "rgba(25, 118, 210, 0.05)" : "#ffffff",
  backgroundImage: active
    ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)"
    : "none",
  boxShadow: active
    ? "0 8px 24px rgba(25, 118, 210, 0.15)"
    : "0 2px 8px rgba(0, 0, 0, 0.06)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: active ? "translateY(-2px)" : "translateY(0)",
  "&:hover": {
    borderColor: active ? "#1976d2" : "rgba(25, 118, 210, 0.4)",
    boxShadow: active
      ? "0 12px 32px rgba(25, 118, 210, 0.2)"
      : "0 4px 16px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-4px)",
    backgroundColor: active ? "rgba(25, 118, 210, 0.08)" : "rgba(25, 118, 210, 0.02)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px",
    gap: "16px",
  },
}));

const IconBox = styled(Box)(({ active }) => ({
  width: "56px",
  height: "56px",
  minWidth: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  backgroundColor: active ? "rgba(25, 118, 210, 0.15)" : "rgba(0, 0, 0, 0.04)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: active ? "rgba(25, 118, 210, 0.25)" : "rgba(0, 0, 0, 0.08)",
    transform: "scale(1.05)",
  },
  "& svg": {
    width: "28px",
    height: "28px",
    color: active ? "#1976d2" : "#718096",
    transition: "color 0.3s ease",
  },
}));

const ModeContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const ModeLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "15px",
  color: "#1a1a1a",
  letterSpacing: "-0.3px",
});

const ModeCaption = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "500",
  fontSize: "13px",
  color: "#718096",
  lineHeight: "1.4",
});

const ActiveBadge = styled(Box)({
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "rgba(25,118,210,0.1)",
  color: "#1976d2",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
});

//
// Component
//
export default function ShippingModeSelector({ mode, setMode }) {
  return (
    <StyledPaper elevation={0}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
      >
        {MODES.map((m) => {
          const active = m.value === mode;

          return (
            <StyledModeCard
              key={m.value}
              onClick={() => setMode(m.value)}
              elevation={0}
              active={active}
            >
              <IconBox active={active}>
                <m.Icon />
              </IconBox>

              <ModeContent>
                <ModeLabel>{m.label}</ModeLabel>
                <ModeCaption>{m.caption}</ModeCaption>
              </ModeContent>

              {active && <ActiveBadge>Active</ActiveBadge>}
            </StyledModeCard>
          );
        })}
      </Stack>
    </StyledPaper>
  );
}
